import React, { useMemo } from 'react'
import { Spinner } from '../shared/Spinner'
import useQuery from '../../hooks/useQuery'
import QUERY_KEYS from '../../graphql/queryKeys'
import resourceTypesQuery from '../../graphql/queries/resourceTypes'
import { captureErrorAndNotify } from '../../utilities/errorHandlers'
import NormalLayoutContainer from '../shared/NormalLayoutContainer'
import PageTitle from '../../components/page-title'
import OnlineScheduleProductDetails from '../../components/online-schedule-product-details'
import styles from './index.module.scss'
import { useSelector } from 'react-redux'
import OnlineSchedulePricingToggle from '../../components/online-schedule-pricing-toggle'
import onlineSchedulePricingQuery from '../../graphql/queries/onlineSchedulePricing'
import onlineScheduleCustomizationsQuery from '../../graphql/queries/onlineScheduleCustomizations'

export default function OnlineScheduleAssetsPage () {
  const { hauler, user } = useSelector(({ user }) => ({ user: user.user, hauler: user.hauler }))

  const { data: { onlineSchedulePricing }, refetch: refetchOnlineSchedulePricing, isFetching: isFetchingOnlineSchedulePricing, isError: isOnlineSchedulePricingError } = useQuery(
    [QUERY_KEYS.onlineSchedulePricing, { haulerId: user.haulerId }],
    onlineSchedulePricingQuery,
    {
      enabled: Boolean(user.haulerId),
      onError (error) {
        captureErrorAndNotify(error, 'Failed to retrieve Online Schedule pricing settings')
      },
      placeholderData: { onlineSchedulePricing: { pricingDisabled: false, pricingMessage: '' } }
    }
  )

  const pricingDisabled = Boolean(onlineSchedulePricing?.pricingDisabled)

  const { data: { onlineScheduleCustomizations: customizations }, isFetching: isFetchingCustomizations, isError: isCustomizationsError } = useQuery(
    [QUERY_KEYS.onlineScheduleCustomizations, { haulerId: user.haulerId }],
    onlineScheduleCustomizationsQuery,
    {
      enabled: Boolean(user.haulerId),
      onError (error) {
        captureErrorAndNotify(error, 'Failed to retrieve Online Schedule customizations')
      },
      placeholderData: { onlineScheduleCustomizations: { defaults: {}, savedResults: {} } }
    }
  )

  const enableCardCapture =
    isCustomizationsError ||
    Boolean(
      customizations?.savedResults?.enableCardCapture ??
      customizations?.defaults?.enableCardCapture
    )

  const { data: { resourceTypes }, isFetching: isFetchingResourceTypes, refetch: refetchAssetTypes } = useQuery(
    [QUERY_KEYS.resourceTypes, { haulerId: user.haulerId, includeCounts: false, includeExtra: false, includeAttributes: true }],
    resourceTypesQuery,
    {
      onError (error) {
        captureErrorAndNotify(error, 'Failed to retrieve resource types')
      },
      placeholderData: { resourceTypes: [] }
    }
  )

  const sortVisibleFirst = useMemo(() => {
    return [...resourceTypes].sort((a, b) => (b.onlineScheduleVisible === true) - (a.onlineScheduleVisible === true))
  }, [resourceTypes])

  function formatMoneyFromCents (money) {
    if (money == null || isNaN(money)) return null

    return (money / 100).toFixed(2)
  }

  if (isFetchingResourceTypes || isFetchingOnlineSchedulePricing) {
    return (<Spinner isFetching />)
  }

  if (isOnlineSchedulePricingError) {
    return (
      <NormalLayoutContainer showBackLink>
        <PageTitle>
          <div className={styles.header}>
            <div>Online Schedule: Set Up Products (Step: 1/3)</div>
          </div>
        </PageTitle>
        <div>Unable to load pricing settings. Please refresh or try again later.</div>
      </NormalLayoutContainer>
    )
  }

  return (
    <NormalLayoutContainer showBackLink>
      <PageTitle>
        <div className={styles.header}>
          <div>Online Schedule: Set Up Products (Step: 1/3)</div>
        </div>
      </PageTitle>
      <div>
        <OnlineSchedulePricingToggle
          haulerId={user.haulerId}
          pricingEnabled={!pricingDisabled}
          enableCardCapture={enableCardCapture}
          cardCaptureLoading={isFetchingCustomizations}
          pricingMessage={onlineSchedulePricing?.pricingMessage ?? ''}
          onPricingUpdated={refetchOnlineSchedulePricing}
        />

        {sortVisibleFirst.map(rt => (
          <OnlineScheduleProductDetails
            key={rt.id}
            assetDetails={rt}
            haulerId={user.haulerId}
            haulerCountry={hauler.country}
            pricingDisabled={pricingDisabled}
            refetchAssetTypes={refetchAssetTypes}
            initialValues={{
              length: rt.resourceAttributes?.length,
              width: rt.resourceAttributes?.width,
              height: rt.resourceAttributes?.height,
              dimensionUom: rt.resourceAttributes?.dimensionUom,
              maxWeight: rt.resourceAttributes?.maxWeight,
              weightUom: rt.resourceAttributes?.weightUom,
              description: rt.onlineScheduleDescription,
              prohibitedMaterials: rt.resourceAttributes?.prohibitedMaterials,
              priceIncludes: rt.onlineSchedulePriceIncludesDetails,
              price: formatMoneyFromCents(rt.onlineScheduleCostCents),
              rentalPeriod: rt.defaultRentalPeriod,
              overageFee: formatMoneyFromCents(rt.resourceAttributes?.onlineScheduleOverageFeeCents),
              extensionFee: formatMoneyFromCents(rt.onlineScheduleExtensionFeeCents),
              assetActive: rt.onlineScheduleVisible ?? false
            }}
          />
        ))}
      </div>

    </NormalLayoutContainer>
  )
}
